import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type Route } from '../../../../store/admin/routesStore';
import {
  getStatusTone,
  getStatusBadgeStyle,
  getStatusBadgeLabel,
} from '../../../../utils/getStatusEntity';
import { getTimeAgo, type TimeTranslations } from '../../../../utils/getDate';
import {
  Badge,
  Button,
  EntityCard,
  EntityCardActions,
  EntityCardBody,
  EntityCardFooter,
  EntityCardHeader,
  EntityCardRow,
} from '../../../../components/ui';
import { CRUDTooltip } from '../../../../components/base/CRUDTooltip';

interface ThemeClasses {
  cardBg: string;
  cardBorder: string;
  text: string;
  textSecondary: string;
  buttonHover: string;
  buttonText: string;
  buttonTextHover: string;
}

interface RoutesCardProps {
  route: Route;
  themeClasses: ThemeClasses;
  onDetailsClick: (route: Route) => void;
  onEditClick?: (route: Route) => void;
  /** Abre el cotizador de costos operativos con esta ruta (sólo si la app está habilitada). */
  onQuoteClick?: (route: Route) => void;
  disableCRUD?: boolean;
}

const formatDuration = (minutes: number | undefined): string => {
  if (!minutes || minutes <= 0) return '-';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

export default function RoutesCard({ route, onDetailsClick, onEditClick, onQuoteClick, disableCRUD = false }: RoutesCardProps) {
  const { t } = useTranslation('adminSections');
  const { t: tLite } = useTranslation('lite');

  const timeTranslations: TimeTranslations = useMemo(
    () => ({
      noData: tLite('time.noData'),
      rightNow: tLite('time.rightNow'),
      min: tLite('time.min'),
      hour: tLite('time.hour'),
      hours: tLite('time.hours'),
      day: tLite('time.day'),
      days: tLite('time.days'),
      month: tLite('time.month'),
      months: tLite('time.months'),
      and: tLite('time.and'),
    }),
    [tLite],
  );

  return (
    <EntityCard>
      <EntityCardHeader
        icon={<i className="ri-route-line" />}
        iconTone="primary"
        title={route.name}
        actions={
          <Badge
            tone={getStatusTone(route.status)}
            style={getStatusBadgeStyle(route.status)}
          >
            {getStatusBadgeLabel(route.status, t, t('units.status.unknown'))}
          </Badge>
        }
      />

      <EntityCardBody>
        {route.description && (
          <p className="line-clamp-3 text-content-secondary">{route.description}</p>
        )}

        <EntityCardRow label={t('routes.fields.points', 'Puntos')}>
          {route.principal_route?.length || 0}
        </EntityCardRow>

        <EntityCardRow label={t('routes.fields.distance')}>
          {route.principal_route_distance?.toFixed(2) || 0} km
        </EntityCardRow>

        <EntityCardRow label={t('routes.fields.duration')}>
          {formatDuration(route.principal_route_duration)}
        </EntityCardRow>

        <EntityCardRow label={t('routes.fields.vehicles')}>
          {route.vehicles_count || 0}
        </EntityCardRow>
      </EntityCardBody>

      <EntityCardActions>
        <CRUDTooltip className="flex-1">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={() => onEditClick?.(route)}
            disabled={disableCRUD}
          >
            {t('routes.actions.edit', 'Editar')}
          </Button>
        </CRUDTooltip>
        <Button size="sm" className="flex-1" onClick={() => onDetailsClick(route)}>
          {t('routes.actions.details', 'Ver detalles')}
        </Button>
        {onQuoteClick && (
          <Button
            variant="outline"
            size="sm"
            iconOnly
            className="shrink-0"
            title={t('routes.actions.quote', 'Cotizar')}
            icon={<i className="ri-calculator-line" />}
            onClick={() => onQuoteClick(route)}
          />
        )}
        <Button
          variant="outline"
          size="sm"
          iconOnly
          disabled
          className="shrink-0"
          title={t('comingSoon', 'Próximamente')}
          icon={<i className="ri-archive-line" />}
        />
        <Button
          variant="dangerSoft"
          size="sm"
          iconOnly
          disabled
          className="shrink-0"
          title={t('comingSoon', 'Próximamente')}
          icon={<i className="ri-delete-bin-line" />}
        />
      </EntityCardActions>

      <EntityCardFooter>
        {t('routes.lastUpdate')}:{' '}
        {route.updatedAt?._seconds
          ? getTimeAgo(route.updatedAt._seconds, timeTranslations)
          : '-'}
      </EntityCardFooter>
    </EntityCard>
  );
}
